/**
 * Контракт шлюза, которого ещё нет в `generated.ts`.
 *
 * Уровневый каталог (Решения Д10–Д11) делается параллельным срезом шлюза, и до
 * его слияния `openapi.json` о новых полях не знает. Клиент не вправе ждать:
 * карман уровня уже спрашивает каталог с `level`, а станция уже раскладывает
 * пресеты по уровням. Поэтому здесь объявлено ровно то, что добавит шлюз, — и
 * НИЧЕГО сверх этого:
 *
 * * `ToolCatalogItem.levels: LevelId[]` — на каких уровнях инструмент
 *   объявлен. Поле аддитивное: прежний `level` остаётся уровнем по умолчанию,
 *   и каталог без `levels` читается как «один уровень, тот самый»;
 * * `CatalogEmptyReason.abstention.no_tool.level` — каталог пуст ПО УРОВНЮ, а не по
 *   области. Два повода пустоты различаются словами: «инструментов этого
 *   уровня пока нет» и «считать не за кого» — разные утверждения, и сводить их
 *   в одну фразу значит скрыть от человека, поможет ли ему смена экрана;
 * * `AnswerIn.level` — уровень вызова. Полномочием он не является (область
 *   по-прежнему из сессии): это выбор СЕЛЕКТОРА того же инструмента, объявленного
 *   на трёх уровнях.
 *
 * Приведения типов здесь одно на модуль и снабжены оговоркой: `as` над
 * сгенерированным типом — это утверждение «сервер уже так умеет», и держать
 * его россыпью по компонентам значило бы потерять список того, что придётся
 * снять после слияния. Снимать — здесь и только здесь: когда `api:types`
 * перепородит `generated.ts` с `levels`, функции ниже станут тождествами, а
 * приведения уйдут.
 */

import type { AiToolCatalog, AiToolItem } from "../../api/client";
import { type LevelId } from "../../lib/levels";
import { defineMessages } from "../../i18n/define";
import { tr } from "../../i18n/runtime";
import { levelWord } from "./levelWords";

const m = defineMessages({
  levelEmptyTitle: {
    id: "answer.catalog.levelEmpty.title",
    defaultMessage: "На этом уровне спрашивать нечего",
    description: "Заголовок пустого кармана ИИ: на выбранном уровне экрана нет инструментов (все роли)",
  },
  levelEmptyText: {
    id: "answer.catalog.levelEmpty.text",
    defaultMessage:
      "Инструментов уровня «{level}» пока нет. Это не отказ по правам: реестр не объявляет здесь ни одного инструмента — спросить можно уровнем выше.",
    description: "Пояснение к пустому карману ИИ; {level} — название уровня экрана строчными (район, хозяйство, контур…)",
  },
  thisScreen: {
    id: "answer.catalog.thisScreen",
    defaultMessage: "этого экрана",
    description: "Подставляется в {level} фразы «Инструментов уровня «…» пока нет», когда уровень экрана неизвестен",
  },
  noDelegationTitle: {
    id: "answer.catalog.noDelegation.title",
    defaultMessage: "Спросить нечего",
    description: "Заголовок пустого кармана ИИ: у аккаунта нет делегации на хозяйство",
  },
  noDelegationText: {
    id: "answer.catalog.noDelegation.text",
    defaultMessage: "У аккаунта нет ни одной действующей делегации на хозяйство: считать не за кого.",
    description: "Пояснение к пустому карману ИИ: аккаунт не уполномочен ни на одно хозяйство",
  },
  unknownTitle: {
    id: "answer.catalog.unknown.title",
    defaultMessage: "Спросить нечего",
    description: "Заголовок пустого кармана ИИ при неизвестной причине пустоты (под ним печатается код причины)",
  },
});

/**
 * Статья словаря «каталог пуст по уровню» — ОДНИМ именем на модуль.
 *
 * Код назвал сервер (`CatalogEmptyReason`), и он не тот, каким его ждал клиент
 * до слияния среза шлюза: не `no_tools_for_level`, а `abstention.no_tool.level`.
 * Расхождение стоило бы дорого молча — непризнанная статья печаталась бы кодом
 * вместо слов, а панель стояла бы «blocked» вместо «empty», то есть экран
 * обещал бы отказ по правам там, где реестр просто ничего не объявил.
 */
const LEVEL_EMPTY = "abstention.no_tool.level";

/** Аддитивная часть `ToolCatalogItem`, которую добавит срез шлюза. */
interface ToolLevelsAhead {
  levels?: LevelId[] | null;
}

/**
 * Уровни, на которых объявлен инструмент.
 *
 * Пустой `levels` НЕ означает «ни одного»: каталог старой сборки поля не несёт
 * вовсе, и единственный уровень такого инструмента — его `level`. Различать
 * «поля нет» и «поле пустое» здесь незачем — обе формы описывают каталог, в
 * котором инструмент живёт на одном уровне, и обе дают тот же ответ.
 */
export function toolLevels(tool: AiToolItem): LevelId[] {
  const declared = (tool as AiToolItem & ToolLevelsAhead).levels;
  if (declared && declared.length > 0) return declared;
  return [tool.level];
}

/**
 * Почему каталог пуст — словами.
 *
 * Код приходит статьёй закрытого словаря (`CatalogEmptyReason`), свободной
 * строки сервер не шлёт. Уровень нужен второй статье: «инструментов уровня
 * «работа» пока нет» отвечает на вопрос «а где тогда спрашивать», а
 * обезличенное «инструментов нет» — нет.
 */
export function emptyReasonWords(
  reason: string,
  level: LevelId | null,
): { title: string; text: string } {
  if (reason === LEVEL_EMPTY) {
    const label = level ? levelWord(level) : tr(m.thisScreen);
    return {
      title: tr(m.levelEmptyTitle),
      text: tr(m.levelEmptyText, { level: label }),
    };
  }
  if (reason === "abstention.forbidden.no_delegation") {
    return {
      title: tr(m.noDelegationTitle),
      text: tr(m.noDelegationText),
    };
  }
  // Статья, которой клиент не знает, печатается КОДОМ, а не молчанием:
  // расширение словаря на сервере обязано быть видно, а не исчезать с экрана.
  return { title: tr(m.unknownTitle), text: reason };
}

/**
 * Каталог пуст по уровню, а не по области.
 *
 * Сравнение идёт через строку: в сгенерированных типах `CatalogEmptyReason`
 * пока перечисляет одну статью, и `tsc` считает сравнение со второй заведомо
 * ложным. Это ровно то расхождение, ради которого файл и заведён, — оно уйдёт
 * вместе с ним.
 */
export function emptyByLevel(catalog: AiToolCatalog): boolean {
  return (catalog.empty_reason as string | null | undefined) === LEVEL_EMPTY;
}
